import { apiClient } from '@/api/client'
import type { LoginRequest, LoginResponse, User, TeamMember, Role } from '@/types'
import { unwrapList } from '@/lib/api-response'
import { dedupeRequest } from '@/lib/dedupe-request'

// Re-export LoginResponse for signup usage
export type { LoginResponse }

export const authApi = {
  login: (data: LoginRequest) =>
    apiClient.post<LoginResponse>('/api/login', data).then((r) => r.data),

  logout: (refreshToken: string) =>
    apiClient.post('/api/logout', { refresh: refreshToken }),

  refreshToken: (refreshToken: string) =>
    apiClient.post<{ access: string }>('/api/token/refresh', { refresh: refreshToken }).then((r) => r.data),

  // Password reset — request a link by email (always 200, never reveals if the
  // email exists), then set a new password with the emailed one-time token.
  requestPasswordReset: (email: string) =>
    apiClient
      .post<{ message: string }>('/api/password-reset/', { email })
      .then((r) => r.data),

  confirmPasswordReset: (data: { token: string; password: string; confirm_password: string }) =>
    apiClient
      .post<{ message: string }>('/api/password-reset/confirm/', data)
      .then((r) => r.data),

  getProfile: () =>
    apiClient.get<{ status: boolean; message: string; data: User }>('/api/users/profile/').then((r) => r.data.data),

  /** Fetch current user's permission IDs (unnati-compatible numeric codes). */
  getMyPermissions: () =>
    dedupeRequest('me/permissions', () =>
      apiClient
        .get<
          | number[]
          | { status: boolean; message: string; data: number[]; is_company_admin?: boolean }
          | { data: number[]; is_company_admin?: boolean }
        >('/api/me/permissions/')
        .then((r) => {
          const raw = r.data
          if (Array.isArray(raw)) {
            return { permissions: raw, is_admin: false }
          }
          const obj = raw as { data?: number[]; is_company_admin?: boolean }
          return {
            permissions: Array.isArray(obj?.data) ? obj.data : [],
            is_admin: !!obj?.is_company_admin,
          }
        }),
    ),

  updateProfile: (userId: number | string, data: Partial<User>) =>
    apiClient.patch<{ status: string; message: string; data: User }>(`/api/users/${userId}/`, data).then((r) => r.data.data),

  // Change the current user's own password (verifies current password server-side).
  changePassword: (data: { current_password: string; new_password: string }) =>
    apiClient
      .post<{ status: string; message: string }>('/api/users/change-password/', data)
      .then((r) => r.data),

  // Team (DRF ViewSet returns paginated results)
  getTeam: () =>
    apiClient.get<{ results: TeamMember[] } | TeamMember[]>('/api/team/').then((r) =>
      unwrapList<TeamMember>(r.data),
    ),

  inviteTeamMember: (data: { email: string; role_id: number | string; first_name?: string; last_name?: string }) =>
    apiClient.post('/api/team/invite/', data).then((r) => r.data),

  // Create an active team member with a password (they can log in immediately).
  // Company is taken from the requesting user on the backend.
  createTeamMember: (data: {
    first_name: string
    last_name: string
    email: string
    phone_number?: string
    role_id: number | string
    password: string
    confirm_password: string
  }) => apiClient.post('/api/team/create_member/', data).then((r) => r.data),

  updateTeamMemberRole: (memberId: string, roleId: number | string) =>
    apiClient.patch(`/api/team/${memberId}/update_role/`, { role_id: roleId }).then((r) => r.data),

  // Edit a member's profile (name + phone). Email/company/role are not editable here.
  updateTeamMember: (
    memberId: string,
    data: { first_name?: string; last_name?: string; phone_number?: string | null },
  ) => apiClient.patch(`/api/team/${memberId}/update_member/`, data).then((r) => r.data),

  // Activate / deactivate a member (login access).
  setTeamMemberActive: (memberId: string, isActive: boolean) =>
    apiClient
      .patch(`/api/team/${memberId}/set_active/`, { is_active: isActive })
      .then((r) => r.data),

  removeTeamMember: (memberId: string) =>
    apiClient.delete(`/api/team/${memberId}/`).then((r) => r.data),

  // Roles (backend wraps in {status, message, data})
  getRoles: async () => {
    const r = await apiClient.get<
      | { status: boolean; message: string; data: Role[]; action_permissions?: Record<string, string[]> }
      | Role[]
    >('/api/role/')
    const raw = r.data
    if (Array.isArray(raw)) {
      return { results: raw, action_permissions: undefined }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wrapped = raw as any
    return {
      results: (wrapped?.data ?? wrapped?.results ?? []) as Role[],
      action_permissions: wrapped?.action_permissions,
    }
  },

  getRolePermissionsMatrix: (roleId: string) =>
    apiClient
      .get<{ status: boolean; data: unknown[] }>(`/api/role/${roleId}/permissions-matrix/`)
      .then((r) => r.data.data as import('@/lib/permissions').RolePermissionModule[]),

  setRolePermissions: (roleId: string, permissions: number[]) =>
    apiClient
      .post(`/api/role/${roleId}/set-permissions/`, { permissions })
      .then((r) => r.data),

  createRole: (data: { name: string; display_name: string; description?: string; permissions?: string[] }) =>
    apiClient.post('/api/role/', data).then((r) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = r.data as any
      return raw?.data ?? raw
    }),

  updateRole: (roleId: string, data: Partial<{ name: string; display_name: string; description: string; permissions: string[] }>) =>
    apiClient.put(`/api/role/${roleId}/`, data).then((r) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = r.data as any
      return raw?.data ?? raw
    }),

  deleteRole: (roleId: string) =>
    apiClient.delete(`/api/role/${roleId}/`).then((r) => r.data),

  getPermissions: () =>
    apiClient.get('/api/permissions/').then((r) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = r.data as any
      return raw?.data ?? raw?.results ?? raw ?? []
    }),

  /** Codename → numeric ID map for route/sidebar permission checks */
  getPermissionMap: () =>
    dedupeRequest('permissions/map', () =>
      apiClient
        .get<{ status: boolean; data: Record<string, number> }>('/api/permissions/map/')
        .then((r) => r.data?.data ?? {}),
    ),

  signup: (data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    company_name: string;
    source?: string;
    source_detail?: string;
  }) => apiClient.post<LoginResponse>('/api/signup', data).then((r) => r.data),
}
